/**
 * placement-versicherer.ts — the Versicherer-Ranking & Auswahl contract (P2 Placement Slice 7,
 * Ausschreibungs-Screen Tab 3a, Prozessschritt 5; Detailkonzept Ausschreibung V3.0 Ch 6.5 / 7.3 / 8.1).
 *
 * The SINGLE SOURCE OF TRUTH for:
 *   - the Eignung / Auswahl-Status / Stats-Ausloeser vocabularies (triad idiom),
 *   - the ranking order (`sortVersichererRanking`, pure — D2: Eignung tier → Platzierungshistorie →
 *     deterministic tie-breaks),
 *   - the selection gate (`isVersichererAuswahlErlaubt`) + the PUT-replace diff (`diffVersichererAuswahl`),
 *   - the header-chip projection (`chipsFromAuswahl`),
 *   - the wire shapes (ranking, auswahl, chip, PUT body).
 *
 * The api owns every decision (stats recompute, gating, selection writes); the web only renders +
 * triggers. Pure module — no side effects, no I/O.
 *
 * [Source: docs/product/p2-slices/slice-7-versicherer-ranking-design.md §3]
 * [Source: packages/shared/src/police-risiko.ts] (triad + labelled-enum idioms)
 * [Source: packages/shared/src/placement.ts] (PlacementStatus + the status-step helper)
 */

import { placementStatusStep } from './placement';
import type { PlacementStatus, PlacementClosureStatus } from './placement';
import type { SubmissionStatus } from './submission';

// ── Vocabularies (triad idiom) ───────────────────────────────────────────────

/** The Eignung tier of a Versicherer for a placement's Sparte (Ch 8.1 Ausschlusskriterium). */
export const VERSICHERER_EIGNUNG = {
  GEEIGNET: 'geeignet',
  UNBEKANNT: 'unbekannt',
  AUSGESCHLOSSEN: 'ausgeschlossen',
} as const;
export type VersichererEignung = (typeof VERSICHERER_EIGNUNG)[keyof typeof VERSICHERER_EIGNUNG];
export const VERSICHERER_EIGNUNG_VALUES: readonly VersichererEignung[] = [
  VERSICHERER_EIGNUNG.GEEIGNET,
  VERSICHERER_EIGNUNG.UNBEKANNT,
  VERSICHERER_EIGNUNG.AUSGESCHLOSSEN,
];
export const VERSICHERER_EIGNUNG_LABELS: Record<VersichererEignung, string> = {
  [VERSICHERER_EIGNUNG.GEEIGNET]: 'Geeignet - Sparte im Bestand',
  [VERSICHERER_EIGNUNG.UNBEKANNT]: 'Keine Historie in dieser Sparte',
  [VERSICHERER_EIGNUNG.AUSGESCHLOSSEN]: 'Nicht geeignet',
};

/** The lifecycle status of one placement_carrier_selection row. */
export const VERSICHERER_AUSWAHL_STATUS = {
  AUSGEWAEHLT: 'ausgewaehlt',
  ENTFERNT: 'entfernt',
  INAKTIV: 'inaktiv',
} as const;
export type VersichererAuswahlStatus =
  (typeof VERSICHERER_AUSWAHL_STATUS)[keyof typeof VERSICHERER_AUSWAHL_STATUS];
export const VERSICHERER_AUSWAHL_STATUS_VALUES: readonly VersichererAuswahlStatus[] = [
  VERSICHERER_AUSWAHL_STATUS.AUSGEWAEHLT,
  VERSICHERER_AUSWAHL_STATUS.ENTFERNT,
  VERSICHERER_AUSWAHL_STATUS.INAKTIV,
];
export const VERSICHERER_AUSWAHL_STATUS_LABELS: Record<VersichererAuswahlStatus, string> = {
  [VERSICHERER_AUSWAHL_STATUS.AUSGEWAEHLT]: 'ausgewählt',
  [VERSICHERER_AUSWAHL_STATUS.ENTFERNT]: 'entfernt',
  [VERSICHERER_AUSWAHL_STATUS.INAKTIV]: 'inaktiv',
};

/** What triggered a stats recompute (carrier_placement_stats_run.ausloeser). */
export const VERSICHERER_STATS_AUSLOESER = {
  ERSTSTART: 'erststart',
  VERALTET: 'veraltet',
  MANUELL: 'manuell',
} as const;
export type VersichererStatsAusloeser =
  (typeof VERSICHERER_STATS_AUSLOESER)[keyof typeof VERSICHERER_STATS_AUSLOESER];
export const VERSICHERER_STATS_AUSLOESER_VALUES: readonly VersichererStatsAusloeser[] = [
  VERSICHERER_STATS_AUSLOESER.ERSTSTART,
  VERSICHERER_STATS_AUSLOESER.VERALTET,
  VERSICHERER_STATS_AUSLOESER.MANUELL,
];
export const VERSICHERER_STATS_AUSLOESER_LABELS: Record<VersichererStatsAusloeser, string> = {
  [VERSICHERER_STATS_AUSLOESER.ERSTSTART]: 'Erstberechnung',
  [VERSICHERER_STATS_AUSLOESER.VERALTET]: 'Automatisch aktualisiert',
  [VERSICHERER_STATS_AUSLOESER.MANUELL]: 'Manuell aktualisiert',
};

// ── Constants ────────────────────────────────────────────────────────────────

/** Max carrier_uids per PUT auswahl body (DTO @ArrayMaxSize + service guard). */
export const VERSICHERER_AUSWAHL_MAX = 50;
/** Sentinel branchencode for policies without an igb2b_code (Ch 6.5). */
export const VERSICHERER_STATS_OHNE_SPARTE_CODE = '00000';
/** The placement statuses in which the Versicherer-Auswahl may be edited (D3). */
export const VERSICHERER_AUSWAHL_ERLAUBTE_STATUS: readonly PlacementStatus[] = [
  'vorgaben_erfasst',
  'ausgeschrieben',
];
/** Eignung tier → sort rank (lower sorts first; D2 tier 1). */
export const VERSICHERER_EIGNUNG_RANG: Record<VersichererEignung, number> = {
  geeignet: 0,
  unbekannt: 1,
  ausgeschlossen: 2,
};

/** Tile warning when a carrier has no Ausschreibungs-E-Mail (Q55 — warning, not Eignung). */
export const VERSICHERER_KEINE_EMAIL_HINWEIS =
  'Keine Ausschreibungs-E-Mail hinterlegt - vor dem Versand beim Versicherer ergänzen.';
/** Empty-state when the tenant's book has no carrier yet (Q54). */
export const VERSICHERER_UNIVERSUM_LEER_HINWEIS =
  'Noch keine Versicherer im Bestand - Versicherer erscheinen, sobald Policen mit Versicherer erfasst sind.';
/** Hint when the Soll-Deckung step is not yet closed (Ch 7.3). */
export const VERSICHERER_VORGABEN_OFFEN_HINWEIS =
  'Die Versicherer-Auswahl ist erst nach «Soll-Deckung abschliessen» möglich.';
/** Next-step hint below the grid (Slice 8 owns the send). */
export const VERSICHERER_NAECHSTER_SCHRITT_HINWEIS =
  'Nächster Schritt: Offertanfragen erstellen und versenden (folgt in Slice 8).';

// ── Wire types ─────────────────────────────────────────────────────────────────

/** A (total/aktiv/letzte) Kennzahl triple. `letzte_platzierung_am` is an ISO date (yyyy-mm-dd). */
export interface VersichererKennzahl {
  anzahl_policen_total: number;
  anzahl_policen_aktiv: number;
  letzte_platzierung_am: string | null;
}

/** One ranked Versicherer tile row. */
export interface VersichererRankingEintrag {
  rang: number;
  carrier_uid: string;
  name: string | null;
  logo_url: string | null;
  email_submission: string | null;
  submission_faehig: boolean;
  eignung: VersichererEignung;
  eignung_gruende: string[];
  /** exact placement branchencode. */
  sparte: VersichererKennzahl;
  /** the IGB2B family roll-up (the sort key). */
  familie: VersichererKennzahl;
  /** all Sparten incl. the '00000' sentinel. */
  gesamt: VersichererKennzahl;
  ausgewaehlt: boolean;
  auswahl_status: VersichererAuswahlStatus | null;
  /** gesperrt = submission_id set (a Slice-8 submission exists for the row). */
  auswahl_gesperrt: boolean;
}

/**
 * Why a (forced) recompute was skipped (#14B). `cooldown` = a forced refresh within the 2-minute
 * min-interval; `lock` = the advisory lock was held by another request; `aktuell` = a not-forced
 * read found fresh stats; `null` = a recompute ran (or none was requested).
 */
export type VersichererStatsSkipGrund = 'cooldown' | 'lock' | 'aktuell' | null;

/** The stats freshness marker surfaced on the wire. */
export interface VersichererStatsInfo {
  computed_at: string | null;
  stale: boolean;
  policies_counted: number;
  carriers_counted: number;
  ausloeser: VersichererStatsAusloeser | null;
  /**
   * Did THIS request actually recompute the Kennzahlen? `false` when the request skipped the
   * recompute (already fresh) OR could not take the advisory lock (another request is computing).
   * The web uses `refreshed === false` + a requested refresh to show «läuft bereits / wird gerade
   * berechnet» instead of a false success (Slice 7 lock-contention fix).
   */
  refreshed: boolean;
  /** #14B: why the recompute was skipped (cooldown / lock / aktuell), else null. */
  skip_grund: VersichererStatsSkipGrund;
  /**
   * #14B: ISO timestamp from which a forced refresh is allowed again (`computed_at + 2 min`) when
   * `skip_grund === 'cooldown'`; null otherwise. The web disables the button until then.
   */
  naechste_aktualisierung_ab: string | null;
}

/** Why the Auswahl is blocked (null = allowed). */
export type VersichererAuswahlSperrgrund = 'vorgaben_nicht_erfasst' | 'placement_locked';

/** GET /placements/:id/versicherer/ranking response. */
export interface VersichererRankingResponse {
  placement_id: string;
  status: PlacementStatus;
  branchencode: string;
  branchen_label: string;
  familie: string;
  familie_label: string;
  auswahl_erlaubt: boolean;
  auswahl_sperrgrund: VersichererAuswahlSperrgrund | null;
  stats: VersichererStatsInfo;
  eignung_quelle: string;
  /** sorted, rang 1..n; `ausgeschlossen` entries are NOT included. */
  versicherer: VersichererRankingEintrag[];
}

/** One header Versicherer-chip (Slice 4 slot, filled by Slice 7). */
export interface PlacementCarrierChip {
  carrier_uid: string;
  name: string | null;
  logo_url: string | null;
  status: VersichererAuswahlStatus;
  status_label: string;
  /**
   * Slice-8 overlay (optional — `chipsFromAuswahl` and every Slice-7 spec compile unchanged).
   * Set by `chipsMitSubmission` when a submission exists for the carrier.
   */
  submission_status?: SubmissionStatus | null;
  submission_number?: string | null;
  frist_date?: string | null;
  frist_abgelaufen?: boolean;
}

/** One placement_carrier_selection row on the wire (the Auswahl list). */
export interface VersichererAuswahlEintrag {
  id: string;
  carrier_uid: string;
  name: string | null;
  logo_url: string | null;
  status: VersichererAuswahlStatus;
  rang_bei_auswahl: number | null;
  eignung_bei_auswahl: VersichererEignung;
  aktive_policen_bei_auswahl: number;
  selected_at: string;
  selected_by: string | null;
  selected_by_name: string | null;
  removed_at: string | null;
  submission_id: string | null;
}

/** GET /auswahl + PUT /auswahl response. */
export interface VersichererAuswahlResponse {
  placement_id: string;
  status: PlacementStatus;
  auswahl_erlaubt: boolean;
  auswahl_sperrgrund: VersichererAuswahlSperrgrund | null;
  /** ALL rows (incl. entfernt), selected_at DESC. */
  items: VersichererAuswahlEintrag[];
  /** ausgewaehlt + inaktiv only, rang_bei_auswahl ASC NULLS LAST then name. */
  chips: PlacementCarrierChip[];
  changed: boolean;
  added: string[];
  removed: string[];
}

/** PUT /placements/:id/versicherer/auswahl body. */
export interface VersichererAuswahlPutBody {
  carrier_uids: string[];
}

// ── Pure helpers ──────────────────────────────────────────────────────────────

/** The subset of a ranking entry the sort depends on. */
export type VersichererRankingSortInput = Pick<
  VersichererRankingEintrag,
  'eignung' | 'familie' | 'gesamt' | 'name' | 'carrier_uid'
>;

/** Compare two ISO dates DESC with NULLS LAST (null sorts after any value). */
function compareDateDescNullsLast(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a < b ? 1 : a > b ? -1 : 0;
}

/**
 * Sort a carrier ranking by the D2 order (returns a sorted COPY; input not mutated):
 *   (1) Eignung tier (geeignet < unbekannt < ausgeschlossen)
 *   (2) familie.anzahl_policen_aktiv DESC
 *   (3) familie.letzte_platzierung_am DESC NULLS LAST
 *   (4) familie.anzahl_policen_total DESC
 *   (5) gesamt.anzahl_policen_aktiv DESC
 *   (6) name ASC (localeCompare de-CH; null name sorts last)
 *   (7) carrier_uid ASC
 */
export function sortVersichererRanking<T extends VersichererRankingSortInput>(
  items: readonly T[],
): T[] {
  return [...items].sort((a, b) => {
    const tier = VERSICHERER_EIGNUNG_RANG[a.eignung] - VERSICHERER_EIGNUNG_RANG[b.eignung];
    if (tier !== 0) return tier;
    const aktiv = b.familie.anzahl_policen_aktiv - a.familie.anzahl_policen_aktiv;
    if (aktiv !== 0) return aktiv;
    const letzte = compareDateDescNullsLast(a.familie.letzte_platzierung_am, b.familie.letzte_platzierung_am);
    if (letzte !== 0) return letzte;
    const total = b.familie.anzahl_policen_total - a.familie.anzahl_policen_total;
    if (total !== 0) return total;
    const gesamtAktiv = b.gesamt.anzahl_policen_aktiv - a.gesamt.anzahl_policen_aktiv;
    if (gesamtAktiv !== 0) return gesamtAktiv;
    // name ASC (null sorts last); de-CH collation.
    if (a.name === null && b.name === null) {
      // fall through to uid
    } else if (a.name === null) {
      return 1;
    } else if (b.name === null) {
      return -1;
    } else {
      const byName = a.name.localeCompare(b.name, 'de-CH');
      if (byName !== 0) return byName;
    }
    return a.carrier_uid < b.carrier_uid ? -1 : a.carrier_uid > b.carrier_uid ? 1 : 0;
  });
}

/**
 * Is the Versicherer-Auswahl editable for this placement state? Returns null when allowed, else the
 * Sperrgrund (D3): allowed only while status ∈ {vorgaben_erfasst, ausgeschrieben} AND closure offen.
 *   - closure erledigt / abgebrochen / any status past ausgeschrieben → 'placement_locked'
 *   - entwurf / risikoprofil_erfasst (before vorgaben_erfasst) → 'vorgaben_nicht_erfasst'
 */
export function isVersichererAuswahlErlaubt(
  status: PlacementStatus,
  closure: PlacementClosureStatus,
): VersichererAuswahlSperrgrund | null {
  if (closure === 'erledigt') return 'placement_locked';
  if (status === 'abgebrochen') return 'placement_locked';
  const step = placementStatusStep(status);
  if (step > placementStatusStep('ausgeschrieben')) return 'placement_locked';
  if (step < placementStatusStep('vorgaben_erfasst')) return 'vorgaben_nicht_erfasst';
  return null;
}

/**
 * Project the header chips from the Auswahl rows: keep only `ausgewaehlt` + `inaktiv` (drop
 * `entfernt`), order by rang_bei_auswahl ASC NULLS LAST then name (de-CH), label from the triad.
 */
export function chipsFromAuswahl(items: readonly VersichererAuswahlEintrag[]): PlacementCarrierChip[] {
  return items
    .filter(
      (i) =>
        i.status === VERSICHERER_AUSWAHL_STATUS.AUSGEWAEHLT ||
        i.status === VERSICHERER_AUSWAHL_STATUS.INAKTIV,
    )
    .sort((a, b) => {
      const ra = a.rang_bei_auswahl;
      const rb = b.rang_bei_auswahl;
      if (ra !== rb) {
        if (ra === null) return 1;
        if (rb === null) return -1;
        return ra - rb;
      }
      const na = a.name;
      const nb = b.name;
      if (na === nb) return a.carrier_uid < b.carrier_uid ? -1 : a.carrier_uid > b.carrier_uid ? 1 : 0;
      if (na === null) return 1;
      if (nb === null) return -1;
      return na.localeCompare(nb, 'de-CH');
    })
    .map((i) => ({
      carrier_uid: i.carrier_uid,
      name: i.name,
      logo_url: i.logo_url,
      status: i.status,
      status_label: VERSICHERER_AUSWAHL_STATUS_LABELS[i.status],
    }));
}

/** The current-row shape the diff needs. */
export interface VersichererAuswahlDiffRow {
  carrier_uid: string;
  status: VersichererAuswahlStatus;
  submission_id: string | null;
}

/**
 * The PUT-replace diff (D3) between the current selection rows and the wanted uid set:
 *   - add        = wanted uid with no row yet → INSERT
 *   - reactivate = wanted uid whose row is `entfernt` → re-activate
 *   - remove     = `ausgewaehlt` row missing from wanted AND submission_id IS NULL → entfernt
 *   - locked     = `ausgewaehlt` row missing from wanted BUT submission_id IS NOT NULL → 409
 * Order-preserving (wanted order for add/reactivate; current order for remove/locked), dedupe-safe.
 */
export function diffVersichererAuswahl(
  current: readonly VersichererAuswahlDiffRow[],
  wanted: readonly string[],
): { add: string[]; reactivate: string[]; remove: string[]; locked: string[] } {
  const byUid = new Map(current.map((c) => [c.carrier_uid, c]));
  const wantedSet = new Set(wanted);
  const add: string[] = [];
  const reactivate: string[] = [];
  const remove: string[] = [];
  const locked: string[] = [];

  const seen = new Set<string>();
  for (const uid of wanted) {
    if (seen.has(uid)) continue;
    seen.add(uid);
    const cur = byUid.get(uid);
    if (!cur) {
      add.push(uid);
    } else if (cur.status === VERSICHERER_AUSWAHL_STATUS.ENTFERNT) {
      reactivate.push(uid);
    }
    // ausgewaehlt / inaktiv already present → no-op
  }
  for (const cur of current) {
    if (cur.status === VERSICHERER_AUSWAHL_STATUS.AUSGEWAEHLT && !wantedSet.has(cur.carrier_uid)) {
      if (cur.submission_id != null) locked.push(cur.carrier_uid);
      else remove.push(cur.carrier_uid);
    }
  }
  return { add, reactivate, remove, locked };
}
